// frontend/src/context/AuthContext.js
import React, { createContext, useState, useEffect } from 'react';
import authService from '../services/auth/authService';
import creditService from '../services/credit/creditService';

export const AuthContext = createContext({
  user: null,
  login: () => {},
  logout: () => {},
  loading: true,
  creditBalance: 0,
  updateCreditBalance: () => {}
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creditBalance, setCreditBalance] = useState(0);

  // Initialize authentication on mount
  useEffect(() => {
    const initializeAuth = async () => {
      // Initialize the auth service
      await authService.initialize();
      
      // Add auth state change listener
      const removeListener = authService.addAuthStateChangeListener((event) => {
        if (event.type === 'SIGNED_IN') {
          setUser(event.user);
          // Fetch credit balance when signed in
          fetchCreditBalance();
        } else if (event.type === 'SIGNED_OUT') {
          setUser(null);
          setCreditBalance(0);
        } else if (event.type === 'TOKEN_REFRESHED') {
          // Just update the user object if needed
          setUser(event.user);
        }
      });
      
      // Check if user is already authenticated
      const currentUser = await authService.getCurrentUser();
      if (currentUser) {
        setUser(currentUser);
        fetchCreditBalance();
      }
      
      setLoading(false);
      
      // Clean up listener on unmount
      return () => removeListener();
    };
    
    initializeAuth();
    
    // Clean up auth service when component unmounts
    return () => {
      authService.cleanup();
    };
  }, []);

  // Fetch credit balance
  const fetchCreditBalance = async () => {
    try {
      const balance = await creditService.getCreditBalance();
      setCreditBalance(balance);
    } catch (error) {
      console.error("Error fetching credit balance:", error);
    }
  };

  // Define login and logout functions
  const login = (userData) => {
    setUser(userData);
  };

  const logout = async () => {
    try {
      await authService.signOut();
      setUser(null);
      setCreditBalance(0);
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  // Add updateCreditBalance function
  const updateCreditBalance = async () => {
    await fetchCreditBalance();
  };

  const value = {
    user,
    login,
    logout,
    loading,
    creditBalance,
    updateCreditBalance
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;